import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Loader2, Copy, CheckCircle2, Download, X, Upload, File, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PresentLevels = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractingData, setExtractingData] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const [formData, setFormData] = useState({
    student_name: '',
    grade_level: '',
    areas_of_need: [],
    current_observations: '',
    strengths: '',
    challenges: '',
    assessment_data: '',
    data_explanation: ''  // New field for explaining uploaded data
  });

  const gradeOptions = [
    'ECE (Early Childhood)', 'Kindergarten', 
    '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade', 
    '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'YA (Young Adult 18-22)'
  ];

  const areaOptions = [
    'Social Skills', 'Emotional Regulation', 'Communication', 'Reading/Literacy',
    'Phonics', 'Fluency', 'Reading Comprehension', 'Written Language',
    'Math Calculation', 'Math Applications', 'Fine Motor', 'Gross Motor',
    'Executive Functioning', 'Attention/Focus', 'Behavior', 'Self-Advocacy',
    'Adaptive Skills', 'Activities of Daily Living', 'Sensory Processing',
    'Adult Transition', 'Pre-Vocational Skills'
  ];

  const toggleAreaOfNeed = (area) => {
    setFormData(prev => ({
      ...prev,
      areas_of_need: prev.areas_of_need.includes(area)
        ? prev.areas_of_need.filter(a => a !== area)
        : [...prev.areas_of_need, area]
    }));
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF, JPG, PNG, or WEBP file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File size must be less than 10MB');
      return;
    }
    setUploadedFile(file);
    setExtractedData(null);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    setExtractedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const extractDataFromFile = async () => {
    if (!uploadedFile) return;
    
    setExtractingData(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadedFile);
      
      const response = await axios.post(`${API}/extract-assessment-data`, formDataUpload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setExtractedData(response.data.extraction);
      
      // Auto-populate assessment data field with extracted summary
      const extraction = response.data.extraction;
      if (extraction?.extracted_data?.raw_text_summary) {
        setFormData(prev => ({
          ...prev,
          assessment_data: prev.assessment_data 
            ? `${prev.assessment_data}\n\n--- Extracted from ${uploadedFile.name} ---\n${extraction.extracted_data.raw_text_summary}`
            : extraction.extracted_data.raw_text_summary
        }));
      }
      
      toast.success('Assessment data extracted successfully!');
    } catch (error) {
      console.error('Error extracting data:', error);
      toast.error('Failed to extract data from file');
    } finally {
      setExtractingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.student_name.trim() || !formData.grade_level || formData.areas_of_need.length === 0 || !formData.current_observations.trim() || !formData.assessment_data.trim()) {
      toast.error('Please fill in all required fields including assessment data');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Convert areas array to string for API
      const payload = {
        ...formData,
        area_of_need: formData.areas_of_need.join(', '),
        // Include extracted file data if available
        extracted_file_data: extractedData?.extracted_data?.raw_text_summary || null,
        // Include data explanation for better integration
        data_explanation: formData.data_explanation || null
      };
      const response = await axios.post(`${API}/generate-present-levels`, payload, {
        withCredentials: true
      });
      setResult(response.data);
      toast.success('Present Levels statement generated!');
    } catch (error) {
      console.error('Error generating present levels:', error);
      toast.error('Failed to generate statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    
    const text = formatResultAsText(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatResultAsText = (data) => {
    const pl = data.present_levels;
    let text = `PRESENT LEVELS OF ACADEMIC ACHIEVEMENT AND FUNCTIONAL PERFORMANCE (PLAAFP)\n`;
    text += `Student: ${data.student_name}\n`;
    text += `Grade: ${data.grade_level}\n`;
    text += `Areas of Need: ${data.area_of_need}\n\n`;
    
    text += `STUDENT OVERVIEW\n${pl.student_overview}\n\n`;
    
    // Asset-Based Section - Strengths & Assets FIRST
    if (pl.strengths_and_assets) {
      text += `STRENGTHS & ASSETS\n`;
      if (pl.strengths_and_assets.strengths?.length > 0) {
        text += `Strengths:\n`;
        pl.strengths_and_assets.strengths.forEach(s => { text += `• ${s}\n`; });
      }
      if (pl.strengths_and_assets.relative_strengths?.length > 0) {
        text += `Relative Strengths:\n`;
        pl.strengths_and_assets.relative_strengths.forEach(s => { text += `• ${s}\n`; });
      }
      if (pl.strengths_and_assets.interests?.length > 0) {
        text += `Interests & Motivations:\n`;
        pl.strengths_and_assets.interests.forEach(s => { text += `• ${s}\n`; });
      }
      text += `\n`;
    }
    
    if (pl.academic_performance) {
      text += `ACADEMIC PERFORMANCE\n`;
      text += `Current Levels: ${pl.academic_performance.current_levels}\n`;
      text += `Classroom Performance: ${pl.academic_performance.classroom_performance}\n\n`;
    }
    
    if (pl.functional_performance) {
      text += `FUNCTIONAL PERFORMANCE\n`;
      if (pl.functional_performance.social_emotional) {
        text += `Social-Emotional: ${pl.functional_performance.social_emotional}\n`;
      }
      if (pl.functional_performance.behavioral) {
        text += `Behavioral: ${pl.functional_performance.behavioral}\n`;
      }
      if (pl.functional_performance.communication) {
        text += `Communication: ${pl.functional_performance.communication}\n`;
      }
      text += `\n`;
    }
    
    // Legacy strengths field (for backward compatibility)
    if (pl.strengths?.length > 0 && !pl.strengths_and_assets) {
      text += `STRENGTHS\n`;
      pl.strengths.forEach(s => { text += `• ${s}\n`; });
      text += `\n`;
    }
    
    text += `AREAS OF NEED\n`;
    pl.areas_of_need?.forEach(a => { text += `• ${a}\n`; });
    
    text += `\nIMPACT STATEMENT\n${pl.impact_statement}\n`;
    text += `\nBASELINE DATA\n${pl.baseline_data}\n`;
    
    text += `\nRECOMMENDATIONS\n`;
    pl.recommendations?.forEach(r => { text += `• ${r}\n`; });
    
    return text;
  };

  const downloadPDF = () => {
    if (!result) return;
    
    const doc = new jsPDF();
    const pl = result.present_levels;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let yPos = 20;
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PRESENT LEVELS OF ACADEMIC ACHIEVEMENT', margin, yPos);
    yPos += 7;
    doc.text('AND FUNCTIONAL PERFORMANCE (PLAAFP)', margin, yPos);
    yPos += 15;
    
    // Student Info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Student: ${result.student_name}`, margin, yPos);
    yPos += 6;
    doc.text(`Grade: ${result.grade_level}`, margin, yPos);
    yPos += 6;
    doc.text(`Area of Need: ${result.area_of_need}`, margin, yPos);
    yPos += 12;
    
    // Function to add section
    const addSection = (title, content) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(title, margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(content, contentWidth);
      lines.forEach(line => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
      yPos += 5;
    };
    
    // Add sections
    if (pl.student_overview) {
      addSection('STUDENT OVERVIEW', pl.student_overview);
    }
    
    // STRENGTHS & ASSETS FIRST (Asset-Based Approach)
    if (pl.strengths_and_assets) {
      let assetsText = '';
      if (pl.strengths_and_assets.strengths?.length > 0) {
        assetsText += 'Strengths:\n' + pl.strengths_and_assets.strengths.map(s => `• ${s}`).join('\n') + '\n\n';
      }
      if (pl.strengths_and_assets.relative_strengths?.length > 0) {
        assetsText += 'Relative Strengths:\n' + pl.strengths_and_assets.relative_strengths.map(s => `• ${s}`).join('\n') + '\n\n';
      }
      if (pl.strengths_and_assets.interests?.length > 0) {
        assetsText += 'Interests & Motivations:\n' + pl.strengths_and_assets.interests.map(s => `• ${s}`).join('\n');
      }
      if (assetsText) {
        addSection('STRENGTHS & ASSETS', assetsText);
      }
    }
    
    if (pl.academic_performance) {
      let academicText = '';
      if (pl.academic_performance.current_levels) {
        academicText += `Current Levels: ${pl.academic_performance.current_levels}\n\n`;
      }
      if (pl.academic_performance.classroom_performance) {
        academicText += `Classroom Performance: ${pl.academic_performance.classroom_performance}`;
      }
      addSection('ACADEMIC PERFORMANCE', academicText);
    }
    
    if (pl.functional_performance) {
      let funcText = '';
      if (pl.functional_performance.social_emotional) {
        funcText += `Social-Emotional: ${pl.functional_performance.social_emotional}\n\n`;
      }
      if (pl.functional_performance.behavioral) {
        funcText += `Behavioral: ${pl.functional_performance.behavioral}\n\n`;
      }
      if (pl.functional_performance.communication) {
        funcText += `Communication: ${pl.functional_performance.communication}`;
      }
      if (funcText) {
        addSection('FUNCTIONAL PERFORMANCE', funcText);
      }
    }
    
    // Legacy strengths field (for backward compatibility)
    if (pl.strengths?.length > 0 && !pl.strengths_and_assets) {
      addSection('STRENGTHS', pl.strengths.map(s => `• ${s}`).join('\n'));
    }
    
    if (pl.areas_of_need?.length > 0) {
      addSection('AREAS OF NEED', pl.areas_of_need.map(a => `• ${a}`).join('\n'));
    }
    
    if (pl.impact_statement) {
      addSection('IMPACT STATEMENT', pl.impact_statement);
    }
    
    if (pl.baseline_data) {
      addSection('BASELINE DATA FOR GOAL DEVELOPMENT', pl.baseline_data);
    }
    
    if (pl.recommendations?.length > 0) {
      addSection('RECOMMENDATIONS', pl.recommendations.map(r => `• ${r}`).join('\n'));
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by SpEdKit - ${new Date().toLocaleDateString()}`, margin, 285);
    
    doc.save(`Present_Levels_${result.student_name.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF downloaded!');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="present-levels-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-text-secondary hover:text-primary"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-6 py-12 max-w-5xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-pink-500" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-text-primary">
                Present Levels Writer
              </h1>
              <p className="font-body text-text-secondary">
                Generate comprehensive PLAAFP statements for IEP documentation
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-2xl p-8 shadow-card animate-fade-in-up">
            <h2 className="font-heading font-semibold text-xl text-text-primary mb-6">
              Student Information
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="student_name" className="text-text-primary font-medium">
                    Student Name *
                  </Label>
                  <Input
                    id="student_name"
                    placeholder="First name or initials"
                    value={formData.student_name}
                    onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                    className="mt-2"
                    data-testid="student-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="grade_level" className="text-text-primary font-medium">
                    Grade Level *
                  </Label>
                  <Select
                    value={formData.grade_level}
                    onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
                  >
                    <SelectTrigger className="mt-2" data-testid="grade-level-select">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {gradeOptions.map((grade) => (
                        <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-text-primary font-medium">
                  Areas of Need * <span className="text-text-muted font-normal text-sm">(Select all that apply)</span>
                </Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="areas-of-need-select">
                  {formData.areas_of_need.length > 0 && (
                    <div className="w-full flex flex-wrap gap-1 mb-2">
                      {formData.areas_of_need.map((area) => (
                        <span 
                          key={area}
                          className="inline-flex items-center gap-1 bg-pink-100 text-pink-700 px-2 py-1 rounded-full text-sm"
                        >
                          {area}
                          <button
                            type="button"
                            onClick={() => toggleAreaOfNeed(area)}
                            className="hover:bg-pink-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
                    {areaOptions.map((area) => (
                      <label
                        key={area}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          formData.areas_of_need.includes(area)
                            ? 'bg-pink-50 border-pink-300'
                            : 'bg-white border-secondary hover:bg-secondary/30'
                        }`}
                      >
                        <Checkbox
                          checked={formData.areas_of_need.includes(area)}
                          onCheckedChange={() => toggleAreaOfNeed(area)}
                        />
                        <span className="text-sm text-text-primary">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="current_observations" className="text-text-primary font-medium">
                  Current Observations *
                </Label>
                <Textarea
                  id="current_observations"
                  placeholder="Describe the student's current performance. Include specific examples of behaviors, skills, or challenges observed in the classroom..."
                  value={formData.current_observations}
                  onChange={(e) => setFormData({ ...formData, current_observations: e.target.value })}
                  className="mt-2 min-h-[120px]"
                  data-testid="current-observations-input"
                />
              </div>

              <div>
                <Label htmlFor="strengths" className="text-text-primary font-medium">
                  Student Strengths (Optional)
                </Label>
                <Textarea
                  id="strengths"
                  placeholder="What does the student do well? What are their interests and motivations?"
                  value={formData.strengths}
                  onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                  className="mt-2"
                  data-testid="strengths-input"
                />
              </div>

              <div>
                <Label htmlFor="challenges" className="text-text-primary font-medium">
                  Primary Challenges (Optional)
                </Label>
                <Textarea
                  id="challenges"
                  placeholder="What specific skills or behaviors need improvement?"
                  value={formData.challenges}
                  onChange={(e) => setFormData({ ...formData, challenges: e.target.value })}
                  className="mt-2"
                  data-testid="challenges-input"
                />
              </div>

              <div>
                <Label htmlFor="assessment_data" className="text-text-primary font-medium">
                  Assessment Data *
                </Label>
                <Textarea
                  id="assessment_data"
                  placeholder="Include formal or informal assessment results, percentiles, data points, or baseline measurements..."
                  value={formData.assessment_data}
                  onChange={(e) => setFormData({ ...formData, assessment_data: e.target.value })}
                  className="mt-2"
                  data-testid="assessment-data-input"
                />
              </div>

              {/* Data Explanation Field - Shows BEFORE file upload */}
              <div className="bg-pink-50/50 rounded-xl p-4 border border-pink-200">
                <Label htmlFor="data_explanation" className="text-text-primary font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-pink-500" />
                  What data are you uploading? (Optional)
                </Label>
                <p className="text-xs text-text-muted mt-1 mb-2">
                  Describe the document you're about to upload so we can better integrate it into the present levels statement.
                </p>
                <Textarea
                  id="data_explanation"
                  placeholder="e.g., This is a reading fluency assessment from October showing words per minute and accuracy rates..."
                  value={formData.data_explanation}
                  onChange={(e) => setFormData({ ...formData, data_explanation: e.target.value })}
                  className="bg-white"
                  data-testid="data-explanation-input"
                />
              </div>

              {/* File Upload Section with Drag & Drop */}
              <div 
                className={`border-2 border-dashed rounded-xl p-4 transition-all duration-200 ${
                  isDragging 
                    ? 'border-pink-500 bg-pink-50 scale-[1.02]' 
                    : 'border-secondary hover:border-pink-300 hover:bg-pink-50/30'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                data-testid="file-drop-zone"
              >
                <Label className="text-text-primary font-medium mb-2 block">
                  Upload Assessment Document (Optional)
                </Label>
                <p className="text-sm text-text-muted mb-3">
                  Drag & drop a file here, or click to browse. We'll extract the data automatically.
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                
                {!uploadedFile ? (
                  <div 
                    className={`flex flex-col items-center justify-center py-6 cursor-pointer rounded-lg transition-colors ${
                      isDragging ? 'bg-pink-100' : 'bg-gray-50 hover:bg-pink-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${
                      isDragging ? 'bg-pink-200' : 'bg-pink-100'
                    }`}>
                      <Upload className={`w-6 h-6 ${isDragging ? 'text-pink-600' : 'text-pink-500'}`} />
                    </div>
                    <p className="text-sm font-medium text-text-primary">
                      {isDragging ? 'Drop file here!' : 'Drag & drop or click to upload'}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      PDF, JPG, PNG, or WEBP (max 10MB)
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-pink-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <File className="w-5 h-5 text-pink-500" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{uploadedFile.name}</p>
                          <p className="text-xs text-text-muted">
                            {(uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeUploadedFile}
                        className="text-red-500 hover:bg-red-50"
                        data-testid="remove-file-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {!extractedData ? (
                      <Button
                        type="button"
                        onClick={extractDataFromFile}
                        disabled={extractingData}
                        className="w-full bg-pink-500 text-white hover:bg-pink-600"
                        data-testid="extract-data-btn"
                      >
                        {extractingData ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Extracting Data...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            Extract Assessment Data
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-700 mb-2">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm font-medium">Data extracted successfully!</span>
                        </div>
                        <p className="text-xs text-green-600">
                          The extracted data has been added to your Assessment Data field above.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-pink-500 text-white hover:bg-pink-600 rounded-full py-3"
                data-testid="generate-present-levels-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Statement...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Generate Present Levels
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Results */}
          <div className="animate-fade-in-up animation-delay-200">
            {result ? (
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    PLAAFP Statement
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="text-text-secondary"
                      data-testid="copy-present-levels-btn"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadPDF}
                      className="text-text-secondary"
                      data-testid="download-present-levels-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Overview */}
                  {result.present_levels?.student_overview && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Student Overview</h3>
                      <p className="font-body text-text-secondary text-sm bg-secondary/30 rounded-lg p-4">
                        {result.present_levels.student_overview}
                      </p>
                    </div>
                  )}

                  {/* Strengths & Assets (Asset-Based Section - Shows First) */}
                  {result.present_levels?.strengths_and_assets && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-heading font-medium text-green-700 mb-3">Strengths & Assets</h3>
                      <div className="space-y-3">
                        {result.present_levels.strengths_and_assets.strengths?.length > 0 && (
                          <div>
                            <h4 className="font-body font-medium text-green-600 text-sm mb-1">Strengths</h4>
                            <ul className="space-y-1">
                              {result.present_levels.strengths_and_assets.strengths.map((s, i) => (
                                <li key={i} className="font-body text-green-600 text-sm flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.present_levels.strengths_and_assets.relative_strengths?.length > 0 && (
                          <div>
                            <h4 className="font-body font-medium text-green-600 text-sm mb-1">Relative Strengths</h4>
                            <ul className="space-y-1">
                              {result.present_levels.strengths_and_assets.relative_strengths.map((s, i) => (
                                <li key={i} className="font-body text-green-600 text-sm flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.present_levels.strengths_and_assets.interests?.length > 0 && (
                          <div>
                            <h4 className="font-body font-medium text-green-600 text-sm mb-1">Interests & Motivations</h4>
                            <ul className="space-y-1">
                              {result.present_levels.strengths_and_assets.interests.map((s, i) => (
                                <li key={i} className="font-body text-green-600 text-sm flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Academic Performance */}
                  {result.present_levels?.academic_performance && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Academic Performance</h3>
                      <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                        {result.present_levels.academic_performance.current_levels && (
                          <p className="font-body text-blue-700 text-sm">
                            <strong>Current Levels:</strong> {result.present_levels.academic_performance.current_levels}
                          </p>
                        )}
                        {result.present_levels.academic_performance.classroom_performance && (
                          <p className="font-body text-blue-700 text-sm">
                            <strong>Classroom Performance:</strong> {result.present_levels.academic_performance.classroom_performance}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Functional Performance */}
                  {result.present_levels?.functional_performance && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Functional Performance</h3>
                      <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                        {result.present_levels.functional_performance.social_emotional && (
                          <p className="font-body text-purple-700 text-sm">
                            <strong>Social-Emotional:</strong> {result.present_levels.functional_performance.social_emotional}
                          </p>
                        )}
                        {result.present_levels.functional_performance.behavioral && (
                          <p className="font-body text-purple-700 text-sm">
                            <strong>Behavioral:</strong> {result.present_levels.functional_performance.behavioral}
                          </p>
                        )}
                        {result.present_levels.functional_performance.communication && (
                          <p className="font-body text-purple-700 text-sm">
                            <strong>Communication:</strong> {result.present_levels.functional_performance.communication}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Legacy Strengths & Areas of Need (for backward compatibility - only show if no strengths_and_assets) */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {result.present_levels?.strengths?.length > 0 && !result.present_levels?.strengths_and_assets && (
                      <div>
                        <h3 className="font-heading font-medium text-green-700 mb-2">Strengths</h3>
                        <ul className="space-y-1">
                          {result.present_levels.strengths.map((s, i) => (
                            <li key={i} className="font-body text-green-600 text-sm flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.present_levels?.areas_of_need?.length > 0 && (
                      <div>
                        <h3 className="font-heading font-medium text-amber-700 mb-2">Areas of Need</h3>
                        <ul className="space-y-1">
                          {result.present_levels.areas_of_need.map((a, i) => (
                            <li key={i} className="font-body text-amber-600 text-sm flex items-start gap-2">
                              <div className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0 mt-2"></div>
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Impact Statement */}
                  {result.present_levels?.impact_statement && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Impact Statement</h3>
                      <p className="font-body text-text-secondary text-sm bg-red-50 rounded-lg p-4 border-l-4 border-red-300">
                        {result.present_levels.impact_statement}
                      </p>
                    </div>
                  )}

                  {/* Baseline Data */}
                  {result.present_levels?.baseline_data && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Baseline Data for Goal Development</h3>
                      <p className="font-body text-text-secondary text-sm bg-primary/5 rounded-lg p-4">
                        {result.present_levels.baseline_data}
                      </p>
                    </div>
                  )}

                  {/* Recommendations */}
                  {result.present_levels?.recommendations?.length > 0 && (
                    <div>
                      <h3 className="font-heading font-medium text-text-primary mb-2">Recommendations</h3>
                      <ul className="space-y-2">
                        {result.present_levels.recommendations.map((r, i) => (
                          <li key={i} className="font-body text-text-secondary text-sm flex items-start gap-2 bg-secondary/30 rounded-lg p-3">
                            <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium text-primary">
                              {i + 1}
                            </span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-secondary/30 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-pink-500" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  Ready to Write Present Levels
                </h3>
                <p className="font-body text-text-secondary text-sm max-w-sm mx-auto">
                  Enter student information and observations to generate a comprehensive PLAAFP statement.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PresentLevels;
